import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import axios from "../../../axios";
import "../../../stylesheets/sslOrderStatus.css";

export default function SSLOrderStatus() {
    const [orderStatus, setOrderStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const ssl_csr = localStorage.getItem("ssl_csr");
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        fetchOrderStatus();
        window.scrollTo(0, 0);

        window.addEventListener("pagehide", clearStorage);

        return () => {
            window.removeEventListener("pagehide", clearStorage);
        }
    }, []);

    useEffect(() => {
        return () => {
            clearStorage();
        };
    }, [location.pathname]);

    const clearStorage = () => {
        localStorage.removeItem("SSL_ORDERED");
        localStorage.removeItem("SSL_ORDERED_IN_DB");
        localStorage.removeItem("SSL_ORDER_CustomOrderID");
        localStorage.removeItem("SSL_ORDER_DB_ID");
        localStorage.removeItem("SSL_ORDER_TheSSLStoreOrderID");
        localStorage.removeItem("ssl_approved_domain_email");
        localStorage.removeItem("ssl_approved_domain_name");
        localStorage.removeItem("ssl_csr");
        localStorage.removeItem("ssl_product_code");
    };

    const fetchOrderStatus = async () => {
        setLoading(true);

        try {
            const theSSLStoreOrderID = localStorage.getItem("SSL_ORDER_TheSSLStoreOrderID");
            const customOrderID = localStorage.getItem("SSL_ORDER_CustomOrderID");

            if (!theSSLStoreOrderID || !customOrderID) {
                navigate("/user", { replace: true });
                return;
            }

            const res = await axios.post("ssl/order-status", {
                the_ssl_store_order_id: theSSLStoreOrderID,
                custom_order_id: customOrderID,
            });

            // ssl i tvyalner@ pahum enq DB um

            const postedInDB = localStorage.getItem("SSL_ORDERED_IN_DB");

            if (!postedInDB) {
                const postRes = await axios.post("users/sold-ssl", {
                    user: localStorage.getItem("user_id"),
                    the_ssl_store_order_id: theSSLStoreOrderID,
                    custom_order_id: customOrderID,
                    approverEmail: res.data.ApproverEmail,
                    commonName: res.data.CommonName,
                    productName: res.data.ProductName,
                });

                localStorage.setItem("SSL_ORDERED_IN_DB", true);
            }

            /*
                data {
                    OrderStatus {MajorStatus}

                    AdminContact {Email,FirstName,LastName,Phone}
                    ApproverEmail
                    CommonName
                    Country
                    Organization
                    ProductName
                    PurchaseDate
                    State
                    TSSOrganizationId
                    TheSSLStoreOrderID
                }
            */

            setOrderStatus(res.data);
            setError(null);
        } catch (err) {
            console.error("ORDER STATUS ERROR:", err);
            setError("Failed to fetch order status. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const getMajorStatus = () => {
        if (!orderStatus) return "Unknown";
        if (typeof orderStatus.OrderStatus === "string") return orderStatus.OrderStatus;
        return orderStatus.OrderStatus?.MajorStatus || "Unknown";
    };

    const detailsRows = orderStatus ? [
        {
            label: "Admin Contact",
            value: orderStatus.AdminContact
                ? `${orderStatus.AdminContact.FirstName || ""} ${orderStatus.AdminContact.LastName || ""}`.trim() +
                `\n${orderStatus.AdminContact.Email || ""}` +
                `\n${orderStatus.AdminContact.Phone || ""}`
                : "-",
        },
        { label: "Approver Email", value: orderStatus.ApproverEmail || "-" },
        { label: "Common Name", value: orderStatus.CommonName || "-" },
        { label: "Country", value: orderStatus.Country || "-" },
        { label: "Organization", value: orderStatus.Organization || "-" },
        { label: "Product Name", value: orderStatus.ProductName || "-" },
        { label: "Purchase Date", value: orderStatus.PurchaseDate || "-" },
        { label: "State", value: orderStatus.State || "-" },
        { label: "TSS Organization ID", value: orderStatus.TSSOrganizationId || "-" },
        { label: "The SSL Store Order ID", value: orderStatus.TheSSLStoreOrderID || "-" },
    ] : [];

    const downloadSertificate = async () => {
        if (!orderStatus?.TheSSLStoreOrderID) {
            alert("Order ID not available for download.");
            return;
        }

        setDownloading(true);

        try {
            const res = await axios.post(
                "ssl/download-certificate",
                {
                    ssl_store_order_id: orderStatus.TheSSLStoreOrderID,
                }
            );

            const certs = res?.data?.Certificates || [];

            if (!certs.length) {
                alert("No certificates returned from server.");
                return;
            }

            const zip = new JSZip();

            certs.forEach((cert, idx) => {
                const content = cert.FileContent || cert.fileContent || "";
                const filename =
                    cert.FileName ||
                    cert.fileName ||
                    `certificate-${idx + 1}.crt`;

                zip.file(filename, content);
            });

            const zipBlob = await zip.generateAsync({
                type: "blob",
            });

            saveAs(zipBlob, "ssl-certificates.zip");
        } catch (err) {
            console.error("DOWNLOAD CERTIFICATE ERROR:", err);
            alert("Failed to download certificate. Please try again.");
        } finally {
            setDownloading(false);
        }
    };
    
    if (loading) {
        return (
            <div className="SSLOrderStatus">
                <div className="loader">Loading order status...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="SSLOrderStatus">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    return (
        <div className="SSLOrderStatus">
            <h1>SSL Order Status</h1>

            {orderStatus && (
                <div className="status-container">
                    <div className="status-summary">
                        <div className="left">
                            <span className="status-label">SSL Status</span>
                            <strong className="status-value">{getMajorStatus()}</strong>
                        </div>

                        <div className="right">
                            <button onClick={fetchOrderStatus}>
                                <img src="/images/ssl/arrows-rotate-solid-full.svg" alt="arrows-rotate-solid-full.svg" />
                            </button>

                            {orderStatus.OrderStatus?.MajorStatus === 'Active' && (
                                <button onClick={downloadSertificate} disabled={downloading} title="Download certificate">
                                    <img src="/images/ssl/download-solid-full.svg" alt="download-solid-full.svg" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="status-info">
                        <h2>Order Details</h2>
                        <div className="details-table-wrapper">
                            <table className="details-table">
                                <tbody>
                                    {detailsRows.map((row, index) => (
                                        <tr key={index}>
                                            <td className="details-label">{row.label}</td>
                                            <td className="details-value">
                                                {typeof row.value === "string"
                                                    ? row.value.split("\n").map((line, idx, arr) => (
                                                        <span key={idx}>
                                                            {line}
                                                            {idx < arr.length - 1 && <br />}
                                                        </span>
                                                    ))
                                                    : String(row.value)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
